import {
    HubConnection,
    HubConnectionBuilder,
    LogLevel,
    RetryContext,
    HubConnectionState,
    IRetryPolicy,
    HttpError,
} from '@microsoft/signalr';

const ms = require('ms');

const maxReconnectDelay = ms('1m');
const reconnectDelays = [0, ms('2s'), ms('10s'), ms('30s'), maxReconnectDelay];
const ignoreErrorsDelay = ms('5s');
const startDelays = [...reconnectDelays];

const isUnauthorized = (error: Error): boolean => error instanceof HttpError
    && error.statusCode === 401;

const getRetryPolicy = (callback: any): IRetryPolicy => ({
    nextRetryDelayInMilliseconds: (context: RetryContext) => {
        const { elapsedMilliseconds, previousRetryCount, retryReason } = context;
        if (isUnauthorized(retryReason) && elapsedMilliseconds > ignoreErrorsDelay) {
            callback(retryReason);
            return null; // stop reconnecting
        }
        return previousRetryCount < reconnectDelays.length
            ? reconnectDelays[previousRetryCount]
            : maxReconnectDelay;
    },
});

export default class SignalRConnection {
    private connection: HubConnection | null = null;

    constructor(private notificationsEndpoint: string) {}

    public connect(playerId: string, sessionId: string, callback: any): Promise<void> {
        this.connection = new HubConnectionBuilder()
            .withUrl(`${this.notificationsEndpoint}?playerId=${playerId}&sessionId=${sessionId}`)
            .withAutomaticReconnect(getRetryPolicy(callback))
            .configureLogging(LogLevel.None)
            .build();

        this.connection.on('PushMessage', (message: string) => {
            try {
                const parsedMessage = JSON.parse(message);
                callback(null, parsedMessage);
            } catch {
                return callback(null);
            }
        });

        return this.start();
    }

    public disconnect(): void {
        if (this.connection && this.connection.state === HubConnectionState.Connected) {
            this.connection.stop();
        }
    }

    private async start(retryAttempt = 0): Promise<void> {
        try {
            if (!this.connection) {
                return;
            }
            await this.connection.start();
            // eslint-disable-next-line no-console
            console.assert(this.connection.state === HubConnectionState.Connected);
            // eslint-disable-next-line no-console
            console.log('SignalR Connected.');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.assert(this.connection!.state === HubConnectionState.Disconnected);
            const index = Math.min(startDelays.length - 1, retryAttempt);
            setTimeout(() => this.start(retryAttempt + 1), startDelays[index]);
        }
    }
}
